import { Box, Button, Card, CardActions, CardContent, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Poll, User } from '../../types';
import React, { useEffect, useState } from 'react';
import { addDoc, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';

import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

const PollsList: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [polls, setPolls] = useState<Poll[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const hasManagePollsPermission = () => {
    return user?.permissions?.manage_polls || false;
  };

  const hasConductPollsPermission = () => {
    return user?.permissions?.conduct_polls || false;
  };

  const fetchPolls = async (user: User) => {
    setLoading(true);
    setError(null);
    try {
      const pollsCollection = collection(db, 'polls');
      const querySnapshot = await getDocs(pollsCollection);
      
      const typedPolls: Poll[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        title: doc.data().title ?? 'Untitled Poll',
        electionDate: doc.data().electionDate ?? 'No date set',
        createdAt: doc.data().createdAt ?? new Date().toISOString(),
        archived: doc.data().archived ?? false,
        ...doc.data() as Omit<Poll, 'id'>
      }));

      typedPolls.sort(
        (a, b) => a.title.localeCompare(b.title)
      );

      if (user.allowedPolls) {
        setPolls(typedPolls.filter(poll => user.allowedPolls!.includes(poll.id)));
      } else {
        setPolls(typedPolls);
      }
    } catch (error) {
      console.error('Error fetching polls:', error);
      setError('Failed to fetch polls. Please try again later.');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user) {
      fetchPolls(user);
    }
  }, [user]);

  const handleEdit = (pollId: string) => {
    navigate(`/polls/edit/${pollId}`);
  };

  const handleConduct = (pollId: string) => {
    navigate(`/polls/conduct/${pollId}`);
  };

  const handleClone = async (poll: Poll, user: User) => {
    setLoading(true);
    try {
      const clonedPoll: Omit<Poll, 'id'> = {
        ...poll,
        title: `${poll.title} (Clone)`,
        createdAt: new Date().toISOString(),
        archived: false,
        // Reset any poll-specific data that shouldn't be cloned
        responses: [],
        // Add any other fields that need to be reset or modified for the clone
      };
      delete (clonedPoll as any).id; // Remove the id as it will be generated by Firebase

      const pollsCollection = collection(db, 'polls');
      const newPollRef = await addDoc(pollsCollection, clonedPoll);
      
      await fetchPolls(user); // Refresh the polls list
      navigate(`/polls/edit/${newPollRef.id}`);
    } catch (error) {
      console.error('Error cloning poll:', error);
    }
    setLoading(false);
  };

  const handleArchive = async (poll: Poll, user: User) => {
    setLoading(true);
    try {
      const pollRef = doc(db, 'polls', poll.id);
      await updateDoc(pollRef, { archived: true });
      await fetchPolls(user); // Refresh the polls list
    } catch (error) {
      console.error('Error archiving poll:', error);
    }
    setLoading(false);
  };

  const handleUnarchive = async (poll: Poll, user: User) => {
    setLoading(true);
    try {
      const pollRef = doc(db, 'polls', poll.id);
      await updateDoc(pollRef, { archived: false });
      await fetchPolls(user); // Refresh the polls list
    } catch (error) {
      console.error('Error unarchiving poll:', error);
    }
    setLoading(false);
  };

  const handleToggleArchived = () => {
    setShowArchived(!showArchived);
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const filteredPolls = showArchived ? polls : polls.filter(poll => !poll.archived);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Polls</Typography>
        <Box display="flex" alignItems="center">
          <FormControlLabel
            control={<Switch checked={showArchived} onChange={handleToggleArchived} />}
            label="Show Archived"
          />
          {hasManagePollsPermission() && (
            <Button component={Link} to="/polls/new" variant="contained" color="primary">
              New Poll
            </Button>
          )}
        </Box>
      </Box>
      {error && <Typography color="error">{error}</Typography>}
      {filteredPolls.map(poll => (
        <Card key={poll.id} sx={{ mb: 2, opacity: poll.archived ? 0.6 : 1 }}>
          <CardContent>
            <Typography variant="h6">{poll.title ?? 'Untitled Poll'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {poll.description}
            </Typography>
            {poll.archived && (
              <Typography variant="body2" color="error">
                Archived
              </Typography>
            )}
          </CardContent>
          <CardActions>
            {hasManagePollsPermission() && user && (
              <>
                <Button size="small" color="primary" onClick={() => handleEdit(poll.id)} disabled={poll.archived}>
                  Edit
                </Button>
                <Button size="small" color="primary" onClick={() => handleClone(poll, user)}>
                  Clone
                </Button>
                {!poll.archived ? (
                  <Button size="small" color="secondary" onClick={() => handleArchive(poll, user)}>
                    Archive
                  </Button>
                ) : (
                  <Button size="small" color="success" onClick={() => handleUnarchive(poll, user)}>
                    Unarchive
                  </Button>
                )}
              </>
            )}
            {hasConductPollsPermission() && (
              <Button size="small" color="success" onClick={() => handleConduct(poll.id)} disabled={poll.archived}>
                Conduct
              </Button>
            )}
          </CardActions>
        </Card>
      ))}
      {loading && <Box display="flex" justifyContent="center"><CircularProgress /></Box>}
      {!loading && filteredPolls.length === 0 && <Typography align="center">No polls available</Typography>}
    </Box>
  );
};

export default PollsList;
